import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CalendarClock, Edit3, Play, Trash2 } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  buildDashboardDataTableColumns,
  useDashboardDataTableState,
  type DashboardDataTableSortState,
} from '@shigo-ui/dashboard-ui';
import { Button, ConfirmDialog, toast } from '@shigo-ui/ui';

import { formatDisplayDate } from '../../../../../lib/format';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { SaveReportDialog } from '../../../reportExplorer/components/SaveReportDialog';
import { useSavedReportMutations } from '../../hooks';
import {
  ReportDeleteTarget,
  SavedReportSource,
  savedReportSharedUserIds,
  type SavedReportConfig,
  type ScheduledReportConfig,
} from '../../model';
import { ScheduleReportDialog } from '../ScheduleReportDialog/ScheduleReportDialog';
import { ScheduleInfo, SharedWithInfo } from './SavedReportInfoCells';

interface SavedReportsTableProps {
  source: SavedReportSource;
  reports: SavedReportConfig[];
  schedules: ScheduledReportConfig[];
  loading: boolean;
  error: boolean;
  preview?: boolean;
  agencyId?: string | undefined;
  toolbar?: ReactNode;
}

type SavedReportSortKey = 'name' | 'createdOn';

const pageSizes = [5, 10, 25, 50, 100];

export function SavedReportsTable({
  source,
  reports,
  schedules,
  loading,
  error,
  preview = false,
  agencyId,
  toolbar,
}: SavedReportsTableProps) {
  const navigate = useNavigate();
  const { can } = useShigoPermissions();
  const mutations = useSavedReportMutations(agencyId);
  const table = useDashboardDataTableState({
    initialPageSize: 10,
    initialSort: { key: 'createdOn', direction: DashboardDataTableSortDirection.Desc },
    totalItems: reports.length,
    pageSizes,
    disabled: loading,
  });
  const { page, pageSize, sort } = table;
  const [editing, setEditing] = useState<SavedReportConfig | null>(null);
  const [scheduling, setScheduling] = useState<{
    report: SavedReportConfig;
    schedule: ScheduledReportConfig | null;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState(ReportDeleteTarget.Report);
  const [deleting, setDeleting] = useState<SavedReportConfig | null>(null);
  const scheduleByReportId = useMemo(
    () => new Map(schedules.map((schedule) => [schedule.report.id, schedule])),
    [schedules],
  );
  const sortedReports = useMemo(() => sortReports(reports, sort), [reports, sort]);
  const visibleReports = sortedReports.slice(page * pageSize, (page + 1) * pageSize);
  const removingSchedule = deleteTarget === ReportDeleteTarget.Schedule;
  const deleteMutation = removingSchedule ? mutations.deleteSchedule : mutations.delete;
  const owned = source === SavedReportSource.Owned;

  const columns = useMemo(
    () =>
      buildDashboardDataTableColumns<SavedReportConfig>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('savedReports.reportName'),
          value: (report) => report.name,
          sortable: true,
          sortKey: 'name',
        },
        {
          id: 'sharedWith',
          header: dashboardT('savedReports.sharedWith'),
          headerClassName: 'w-32 text-center',
          className: 'w-32 text-center',
          cell: (report) => <SharedWithInfo report={report} />,
        },
        {
          id: 'scheduled',
          header: dashboardT('savedReports.scheduled'),
          headerClassName: 'w-32 text-center',
          className: 'w-32 text-center',
          cell: (report) => <ScheduleInfo schedule={scheduleByReportId.get(report.id)} />,
        },
        {
          id: 'createdOn',
          header: dashboardT('savedReports.created'),
          headerClassName: 'w-40',
          className: 'w-40 whitespace-nowrap',
          sortable: true,
          sortKey: 'createdOn',
          cell: (report) => formatDisplayDate(report.createdOn, { keepInvalid: true }),
        },
        {
          id: 'run',
          header: dashboardT('savedReports.run'),
          headerClassName: 'w-40',
          className: 'w-40',
          cell: (report) => (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={preview}
              onClick={() => {
                void navigate({
                  to: agencyId
                    ? '/a/$agencyId/reporting/report-explorer'
                    : '/reporting/report-explorer',
                  params: { agencyId: agencyId ?? '' },
                  search: {
                    savedReportId: report.id,
                    savedReportSource: source,
                  },
                });
              }}
            >
              <Play className="h-4 w-4" aria-hidden="true" />
              {dashboardT('savedReports.run')}
            </Button>
          ),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          header: dashboardT('savedReports.actions'),
          cell: (report) => {
            if (preview || !owned) return <span aria-hidden="true">-</span>;
            const schedule = scheduleByReportId.get(report.id) ?? null;
            const canSchedule =
              !schedule && (Boolean(agencyId) || can(ShigoPermission.ReportingSavedReportsCreate));
            const canEdit =
              Boolean(agencyId) ||
              (!schedule && can(ShigoPermission.ReportingCustomReportsCreate)) ||
              (Boolean(schedule) && can(ShigoPermission.ReportingSavedReportsEdit));
            const canDelete = Boolean(agencyId) || can(ShigoPermission.ReportingSavedReportsDelete);
            if (!canSchedule && !canEdit && !canDelete) return null;
            return (
              <DashboardTableRowActions
                label={`${dashboardT('savedReports.actions')}: ${report.name}`}
              >
                {canSchedule ? (
                  <DashboardTableRowActionItem
                    onSelect={() => {
                      setScheduling({ report, schedule: null });
                    }}
                  >
                    <CalendarClock className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('savedReports.schedule')}
                  </DashboardTableRowActionItem>
                ) : null}
                {canEdit ? (
                  <DashboardTableRowActionItem
                    onSelect={() => {
                      if (schedule) setScheduling({ report, schedule });
                      else setEditing(report);
                    }}
                  >
                    <Edit3 className="h-4 w-4" aria-hidden="true" />
                    {dashboardT(schedule ? 'savedReports.editScheduled' : 'savedReports.edit')}
                  </DashboardTableRowActionItem>
                ) : null}
                {agencyId && schedule ? (
                  <DashboardTableRowActionItem
                    onSelect={() => {
                      setDeleteTarget(ReportDeleteTarget.Schedule);
                      setDeleting(report);
                    }}
                  >
                    <Trash2 className="text-destructive h-4 w-4" aria-hidden="true" />
                    {dashboardT('savedReports.removeSchedule')}
                  </DashboardTableRowActionItem>
                ) : null}
                {canDelete ? (
                  <DashboardTableRowActionItem
                    onSelect={() => {
                      setDeleteTarget(ReportDeleteTarget.Report);
                      setDeleting(report);
                    }}
                  >
                    <Trash2 className="text-destructive h-4 w-4" aria-hidden="true" />
                    {dashboardT('savedReports.delete')}
                  </DashboardTableRowActionItem>
                ) : null}
              </DashboardTableRowActions>
            );
          },
        },
      ]),
    [agencyId, can, navigate, owned, preview, scheduleByReportId, source],
  );

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <DashboardDataTable
        title={dashboardT(owned ? 'savedReports.tabs.saved' : 'savedReports.tabs.shared')}
        tableLabel={dashboardT(owned ? 'savedReports.tabs.saved' : 'savedReports.tabs.shared')}
        columns={columns}
        toolbar={toolbar}
        items={visibleReports}
        getRowKey={(report) => report.id}
        loading={loading}
        loadingMessage={dashboardT('savedReports.loading')}
        errorMessage={error ? dashboardT('savedReports.loadError') : undefined}
        emptyMessage={dashboardT(owned ? 'savedReports.empty' : 'savedReports.sharedEmpty')}
        minWidthClassName="min-w-[960px]"
        pagination={table.pagination}
        sort={table.sortProps}
      />
      {editing ? (
        <SaveReportDialog
          agencyId={agencyId}
          initialName={editing.name}
          initialSharedWith={savedReportSharedUserIds(editing)}
          pending={mutations.update.isPending}
          onSubmit={async (name, sharedWith) => {
            await mutations.update.mutateAsync({
              id: editing.id,
              payload: {
                name,
                sharedWith,
                report: editing.report,
                chartReport: editing.chartReport,
                settings: editing.settings,
              },
            });
            setEditing(null);
            toast.success(dashboardT('reportExplorer.save.updated'));
          }}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
      {scheduling ? (
        <ScheduleReportDialog
          agencyId={agencyId}
          report={scheduling.report}
          schedule={scheduling.schedule}
          onClose={() => {
            setScheduling(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={dashboardT(
          removingSchedule ? 'savedReports.removeScheduleTitle' : 'savedReports.deleteTitle',
          { name: deleting?.name ?? '' },
        )}
        description={dashboardT(
          removingSchedule
            ? 'savedReports.removeScheduleDescription'
            : 'savedReports.deleteDescription',
        )}
        cancelLabel={dashboardT('savedReports.cancel')}
        confirmLabel={dashboardT(
          removingSchedule ? 'savedReports.removeSchedule' : 'savedReports.delete',
        )}
        pending={deleteMutation.isPending}
        destructive
        onConfirm={() => {
          if (!deleting) return;
          const id = removingSchedule ? scheduleByReportId.get(deleting.id)?.id : deleting.id;
          if (!id) return;
          void deleteMutation
            .mutateAsync(id)
            .then(() => {
              setDeleting(null);
              toast.success(
                dashboardT(
                  removingSchedule ? 'savedReports.scheduleRemoved' : 'savedReports.deleted',
                ),
              );
            })
            .catch((error: unknown) => {
              showRequestError(error, dashboardT('savedReports.deleteError'));
            });
        }}
      />
    </main>
  );
}

function sortReports(reports: SavedReportConfig[], sort: DashboardDataTableSortState | undefined) {
  if (!sort) return reports;
  const direction = sort.direction === DashboardDataTableSortDirection.Asc ? 1 : -1;
  return [...reports].sort((left, right) => {
    const key = sort.key as SavedReportSortKey;
    const leftValue = key === 'name' ? left.name : (left.createdOn ?? '');
    const rightValue = key === 'name' ? right.name : (right.createdOn ?? '');
    return leftValue.localeCompare(rightValue) * direction;
  });
}
